import { format } from "date-fns";

import { es } from "date-fns/locale";

import {
  ChevronRight,
  File,
  Folder,
  FolderOpen,
  FolderPlus,
  HardDrive,
  Trash2,
  Upload,
} from "lucide-react";

import { useRef, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import { Card, CardContent } from "@/components/ui/card";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import EmptyState from "../../../shared/components/EmptyState";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import {
  useBucketsQuery,
  useCreateFolder,
  useDeleteObject,
  useDeleteRecursive,
  useObjectsQuery,
  useUploadFile,
} from "../hooks/useStorageBrowser";

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;

  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const formatDate = (dateString: string) => {
  return format(new Date(dateString), "dd MMM yyyy HH:mm", { locale: es });
};

const StorageBrowserPage = () => {
  const [selectedBucket, setSelectedBucket] = useState("");

  const [prefix, setPrefix] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<{
    bucket: string;

    key: string;

    type: "file" | "folder";
  } | null>(null);

  const [newFolderName, setNewFolderName] = useState("");

  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: buckets, isLoading: isLoadingBuckets } = useBucketsQuery();

  const { data: objectsData, isLoading: isLoadingObjects } = useObjectsQuery(
    selectedBucket,

    prefix,
  );

  const deleteObject = useDeleteObject();

  const deleteRecursive = useDeleteRecursive();

  const createFolder = useCreateFolder();

  const uploadFile = useUploadFile();

  // Auto-select first bucket

  if (buckets && buckets.length > 0 && !selectedBucket) {
    setSelectedBucket(buckets[0]);
  }

  const folders = objectsData?.folders || [];

  const objects = objectsData?.objects || [];

  const hasContent = folders.length > 0 || objects.length > 0;

  const breadcrumbParts = prefix ? prefix.split("/").filter(Boolean) : [];

  const navigateToPrefix = (newPrefix: string) => {
    setPrefix(newPrefix);
  };

  const navigateToBreadcrumb = (index: number) => {
    if (index < 0) {
      setPrefix("");
    } else {
      const newPrefix = breadcrumbParts.slice(0, index + 1).join("/") + "/";

      setPrefix(newPrefix);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file || !selectedBucket) return;

    await uploadFile.mutateAsync({
      bucket: selectedBucket,

      prefix,

      file,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !selectedBucket) return;

    const folderPath = `${prefix}${newFolderName.trim()}/`;

    await createFolder.mutateAsync({
      bucket: selectedBucket,

      path: folderPath,
    });

    setNewFolderName("");

    setShowNewFolderDialog(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    if (deleteTarget.type === "folder") {
      await deleteRecursive.mutateAsync({
        bucket: deleteTarget.bucket,

        prefix: deleteTarget.key,
      });
    } else {
      await deleteObject.mutateAsync({
        bucket: deleteTarget.bucket,

        key: deleteTarget.key,
      });
    }

    setDeleteTarget(null);
  };

  const getFileName = (key: string) => {
    const parts = key.split("/");

    return parts[parts.length - 1] || key;
  };

  const getFolderName = (folderPrefix: string) => {
    const parts = folderPrefix.replace(/\/$/, "").split("/");

    return parts[parts.length - 1] || folderPrefix;
  };

  if (isLoadingBuckets) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Storage Browser"
          subtitle="Explorar archivos en S3"
        />
        <div className="py-12">
          <LoadingOverlay />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Storage Browser"
        subtitle="Explorar archivos en S3"
        actions={
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              onClick={() => setShowNewFolderDialog(true)}
              disabled={!selectedBucket}
              variant="outline"
              className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
            >
              <FolderPlus className="mr-2 h-4 w-4" />
              Nueva carpeta
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={!selectedBucket || uploadFile.isPending}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploadFile.isPending ? "Subiendo..." : "Subir archivo"}
            </Button>
          </div>
        }
      />

      {/* Bucket selector */}
      <div className="flex flex-wrap gap-2">
        {buckets?.map((bucket) => (
          <Button
            key={bucket}
            variant={selectedBucket === bucket ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setSelectedBucket(bucket);

              setPrefix("");
            }}
            className={
              selectedBucket === bucket
                ? "bg-purple-600 text-white hover:bg-purple-700"
                : "border-neutral-700 text-neutral-300 hover:bg-neutral-700"
            }
          >
            <HardDrive className="mr-2 h-4 w-4" />
            {bucket}
          </Button>
        ))}
      </div>

      {/* Breadcrumbs */}
      {selectedBucket && (
        <div className="flex items-center gap-1 text-sm">
          <button
            onClick={() => navigateToBreadcrumb(-1)}
            className="text-purple-400 hover:text-purple-300 hover:underline"
          >
            {selectedBucket}
          </button>
          {breadcrumbParts.map((part, index) => (
            <span key={index} className="flex items-center gap-1">
              <ChevronRight className="h-3 w-3 text-neutral-500" />
              <button
                onClick={() => navigateToBreadcrumb(index)}
                className={
                  index === breadcrumbParts.length - 1
                    ? "text-neutral-200 font-medium"
                    : "text-purple-400 hover:text-purple-300 hover:underline"
                }
              >
                {part}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Content */}
      <Card className="border-neutral-800 bg-neutral-900/50">
        <CardContent className="space-y-4 p-4">
          {isLoadingObjects ? (
            <div className="py-12">
              <LoadingOverlay />
            </div>
          ) : !hasContent ? (
            <EmptyState
              title="Carpeta vacía"
              description="No hay archivos ni carpetas en esta ubicación."
              icon={<FolderOpen className="h-10 w-10" />}
              actions={
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!selectedBucket}
                  variant="outline"
                  className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Subir archivo
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="text-neutral-400">Nombre</TableHead>
                  <TableHead className="text-neutral-400">Tamaño</TableHead>
                  <TableHead className="text-neutral-400">Modificado</TableHead>
                  <TableHead className="text-neutral-400 text-right">
                    Acciones
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {folders.map((folder) => (
                  <TableRow
                    key={folder.prefix}
                    className="border-neutral-800 hover:bg-neutral-800/50 cursor-pointer"
                    onClick={() => navigateToPrefix(folder.prefix)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Folder className="h-4 w-4 text-yellow-400" />
                        <span className="text-neutral-200 font-medium">
                          {getFolderName(folder.prefix)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-neutral-500">—</TableCell>
                    <TableCell className="text-neutral-500">—</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();

                          setDeleteTarget({
                            bucket: selectedBucket,

                            key: folder.prefix,

                            type: "folder",
                          });
                        }}
                        className="text-red-400 hover:bg-red-500/20 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {objects.map((obj) => (
                  <TableRow
                    key={obj.key}
                    className="border-neutral-800 hover:bg-neutral-800/50"
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-400" />
                        <span className="text-neutral-200">
                          {getFileName(obj.key)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-neutral-400">
                      {formatFileSize(obj.size)}
                    </TableCell>
                    <TableCell className="text-neutral-400">
                      {obj.lastModified ? formatDate(obj.lastModified) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setDeleteTarget({
                            bucket: selectedBucket,

                            key: obj.key,

                            type: "file",
                          })
                        }
                        className="text-red-400 hover:bg-red-500/20 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Folder Dialog */}
      <AlertDialog
        open={showNewFolderDialog}
        onOpenChange={setShowNewFolderDialog}
      >
        <AlertDialogContent className="border-neutral-800 bg-neutral-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-100">
              Nueva carpeta
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              Ingresa el nombre de la nueva carpeta.
              {prefix && (
                <span className="mt-1 block text-xs text-neutral-500">
                  Se creará en: {prefix}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="nombre-carpeta"
            className="border-neutral-700 bg-neutral-800 text-neutral-200"
            onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="border-neutral-700 bg-transparent text-neutral-300 hover:bg-neutral-800 hover:text-neutral-100">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || createFolder.isPending}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              {createFolder.isPending ? "Creando..." : "Crear"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent className="border-neutral-800 bg-neutral-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-100">
              {deleteTarget?.type === "folder"
                ? "Eliminar carpeta"
                : "Eliminar archivo"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              {deleteTarget?.type === "folder" ? (
                <>
                  ¿Estás seguro de que deseas eliminar la carpeta{" "}
                  <span className="font-medium text-neutral-200">
                    {deleteTarget ? getFolderName(deleteTarget.key) : ""}
                  </span>{" "}
                  y todo su contenido? Esta acción no se puede deshacer.
                </>
              ) : (
                <>
                  ¿Estás seguro de que deseas eliminar{" "}
                  <span className="font-medium text-neutral-200">
                    {deleteTarget ? getFileName(deleteTarget.key) : ""}
                  </span>
                  ? Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-neutral-700 bg-transparent text-neutral-300 hover:bg-neutral-800 hover:text-neutral-100">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StorageBrowserPage;
